import { Manrope, Source_Serif_4 } from "next/font/google";

/**
 * Self-hosted at BUILD time. next/font/google downloads the files and serves
 * them from our own origin — no runtime request to fonts.googleapis.com or
 * fonts.gstatic.com.
 *
 * That is a consent requirement, not a performance nicety: a runtime font link
 * sends every visitor's IP address to Google before the cookie banner has
 * asked anything, which a German court has already held to be a GDPR breach
 * and which would make the whole Consent Mode stack self-contradictory.
 * The previous _document.tsx did exactly this with Roboto.
 *
 * `subsets` MUST include "greek". Without it every Greek character silently
 * falls back to a system font — which on this site is every page.
 *
 * TWO faces, two ROLES (owner, 2026-09-09 — supersedes the same-day removal):
 * Source Serif 4 carries the READING surfaces (rich-text bodies, section
 * intros, the hero lede — everything .prose-content plus the explicit
 * font-serif ledes); Manrope carries everything STRUCTURAL (headings, nav,
 * CTAs, forms, cards, labels). The editorial pairing suits the clinical
 * content; the boundary keeps the UI crisp.
 */
export const manrope = Manrope({
  subsets: ["greek", "latin"],
  variable: "--font-manrope",
  display: "swap",
});

export const sourceSerif = Source_Serif_4({
  subsets: ["greek", "latin"],
  variable: "--font-source-serif",
  display: "swap",
});

/** Both variables, for the <html> element. */
export const fontVariables = `${manrope.variable} ${sourceSerif.variable}`;

/**
 * Global font-variable definitions — rendered ONCE by _app.
 *
 * Why _app and not only _document: in the pages router, next/font emits its
 * @font-face CSS only for modules reachable from the CLIENT graph; _document
 * is server-only, so initializing the fonts there put the variable classes on
 * <html> while the CSS that defines them never shipped — and the whole site
 * silently rendered the system font (caught 2026-09-06). Defining the
 * variables on :root (rather than relying on the <html> classes) also lets
 * `--font-sans` resolve on <body> itself, not just inside a wrapper.
 */
export const FontGlobals = () => (
  <style
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{
      __html: `:root{--font-manrope:${manrope.style.fontFamily};--font-source-serif:${sourceSerif.style.fontFamily};}`,
    }}
  />
);
